import { httpService } from '@/services/HttpService';
import { getApiErrorMessage } from '@/utils/apiError';
import type {
  AdminPaginatedPromotionsResponse,
  AdminPaginatedPromotionUsagesResponse,
  CreatePromotionPayload,
  Promotion,
  PromotionListFilters,
  PromotionUsagesFilters,
  PromotionValidationRequest,
  PromotionValidationResponse,
  ResolveFraudPayload,
  SuspendFraudPayload,
  UpdatePromotionPayload,
} from '@/types';

const toQueryParams = (filters: Record<string, string | number | undefined>) => {
  const params: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params[key] = String(value);
    }
  });

  return params;
};

const throwApiError = (error: unknown, fallback: string): never => {
  throw new Error(getApiErrorMessage(error, fallback));
};

export function usePromotions() {
  const getActivePromotions = async (): Promise<Promotion[]> => {
    try {
      const response = await httpService.public.get<{ data: Promotion[] }>('/promotions/active');
      return response.data;
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to load active promotions');
    }
  };

  const validatePromotion = async (
    payload: PromotionValidationRequest,
  ): Promise<PromotionValidationResponse> => {
    try {
      const response = await httpService.post<{ data: PromotionValidationResponse }>(
        '/promotions/validate',
        payload,
      );
      return response.data;
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to validate coupon');
    }
  };

  const listPromotions = async (
    filters: PromotionListFilters = {},
  ): Promise<AdminPaginatedPromotionsResponse> => {
    try {
      const params = toQueryParams({
        status: filters.status,
        search: filters.search,
        page: filters.page,
        per_page: filters.per_page,
      });

      return await httpService.get<AdminPaginatedPromotionsResponse>('/admin/promotions', params);
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to load promotions');
    }
  };

  const getPromotion = async (id: string): Promise<Promotion> => {
    try {
      const response = await httpService.get<{ data: Promotion }>(`/admin/promotions/${id}`);
      return response.data;
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to load promotion details');
    }
  };

  const createPromotion = async (payload: CreatePromotionPayload): Promise<Promotion> => {
    try {
      const response = await httpService.post<{ data: Promotion }>('/admin/promotions', payload);
      return response.data;
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to create promotion');
    }
  };

  const updatePromotion = async (id: string, payload: UpdatePromotionPayload): Promise<Promotion> => {
    try {
      const response = await httpService.put<{ data: Promotion }>(`/admin/promotions/${id}`, payload);
      return response.data;
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to update promotion');
    }
  };

  const deletePromotion = async (id: string): Promise<Promotion> => {
    try {
      const response = await httpService.delete<{ data: Promotion }>(`/admin/promotions/${id}`);
      return response.data;
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to deactivate promotion');
    }
  };

  const getPromotionUsages = async (
    id: string,
    filters: PromotionUsagesFilters = {},
  ): Promise<AdminPaginatedPromotionUsagesResponse> => {
    try {
      const params = toQueryParams({
        status: filters.status,
        user_id: filters.user_id,
        order_id: filters.order_id,
        page: filters.page,
        per_page: filters.per_page,
      });

      return await httpService.get<AdminPaginatedPromotionUsagesResponse>(
        `/admin/promotions/${id}/usages`,
        params,
      );
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to load promotion usages');
    }
  };

  const suspendFraud = async (
    id: string,
    payload: SuspendFraudPayload,
  ): Promise<{ promotion: Promotion; reviewId: string | null }> => {
    try {
      const response = await httpService.post<{ data: Promotion; meta?: { review_id?: string } }>(
        `/admin/promotions/${id}/suspend-fraud`,
        payload,
      );

      return {
        promotion: response.data,
        reviewId: response.meta?.review_id ?? null,
      };
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to suspend promotion for fraud review');
    }
  };

  const resolveFraud = async (
    id: string,
    reviewId: string,
    payload: ResolveFraudPayload,
  ): Promise<Promotion> => {
    try {
      const response = await httpService.post<{ data: Promotion }>(
        `/admin/promotions/${id}/fraud-reviews/${reviewId}/resolve`,
        payload,
      );
      return response.data;
    } catch (error: unknown) {
      return throwApiError(error, 'Failed to resolve fraud review');
    }
  };

  return {
    getActivePromotions,
    validatePromotion,
    listPromotions,
    getPromotion,
    createPromotion,
    updatePromotion,
    deletePromotion,
    getPromotionUsages,
    suspendFraud,
    resolveFraud,
  };
}
